import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  IconButton,
  Avatar,
  Skeleton,
  Fade,
  Slide,
  Badge,
  Tooltip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Snackbar,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import {
  History as HistoryIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Visibility as ViewIcon,
  Event as EventIcon,
  Person as PersonIcon,
  MonetizationOn as MoneyIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  AttachMoney as RefundIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon
} from '@material-ui/icons';
import moment from 'moment';
import admin_api from '../../../api/admin';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';

const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: '#12192c',
    minHeight: 'calc(100vh - 100px)',
    padding: '20px',
    color: '#ffffff',
    marginTop: '80px',
    marginRight: '24px',
    transition: 'all 0.3s ease',
  },
  paper: {
    backgroundColor: '#1a2235',
    color: '#ffffff',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    border: '1px solid #2d3748',
  },
  headerCard: {
    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    color: 'white',
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(4),
    boxShadow: '0 10px 30px rgba(139, 92, 246, 0.3)',
  },
  card: {
    backgroundColor: '#1e293b',
    color: '#ffffff',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    border: '1px solid #2d3748',
    height: '100%',
  },
  successButton: {
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: 'white',
    borderRadius: theme.spacing(3),
    textTransform: 'none',
    fontWeight: 500,
    minWidth: 120,
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
      background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
    },
  },
  tableHead: {
    backgroundColor: '#1e293b',
    color: '#8b5cf6',
    fontWeight: 'bold',
    borderBottom: '2px solid #2d3748'
  },
  tableRow: {
    '& .MuiTableCell-root': {
      color: '#e2e8f0',
      borderColor: '#2d3748'
    },
    '&:hover': {
      backgroundColor: 'rgba(139, 92, 246, 0.05)'
    }
  },
  avatar: {
    backgroundColor: '#8b5cf6',
    width: 32,
    height: 32,
    fontSize: '0.8rem'
  },
  dialog: {
    '& .MuiDialog-paper': {
      backgroundColor: '#1a2235',
      color: '#ffffff',
      borderRadius: theme.spacing(2),
      border: '1px solid #2d3748',
      maxWidth: '800px',
      width: '100%'
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: theme.spacing(1),
      '& fieldset': {
        borderColor: '#2d3748',
      },
      '&:hover fieldset': {
        borderColor: '#4a5568',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#8b5cf6',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#a0aec0',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#8b5cf6',
    },
    '& .MuiOutlinedInput-input': {
      color: '#e2e8f0',
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: '#8b5cf6',
    },
  },
  formControl: {
    '& .MuiInputLabel-root': {
      color: '#a0aec0',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#8b5cf6',
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      '& fieldset': {
        borderColor: '#2d3748',
      },
      '&:hover fieldset': {
        borderColor: '#4a5568',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#8b5cf6',
      },
    },
    '& .MuiSelect-select': {
      color: '#e2e8f0',
    },
    '& .MuiSvgIcon-root': {
      color: '#a0aec0',
    },
  }
}));

export default function CancellationHistory() {
  const classes = useStyles();
  const theme = useTheme();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('all');
  
  // Dialog states
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  const { isCollapse } = useCollapseDrawer();
  
  const containerStyle = {
    marginLeft: isCollapse ? `${COLLAPSE_WIDTH + 24}px` : `${DRAWER_WIDTH + 24}px`,
  };

  if (window.innerWidth <= 1024) {
    containerStyle.marginLeft = '24px';
  }

  const fetchCancellationHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await admin_api.get_cancellation_history(page + 1, rowsPerPage, statusFilter);
      
      if (response && response.data && response.data.success) {
        const requestsData = response.data.data || [];
        setRequests(requestsData);
        setTotalCount(response.data.pagination?.totalRecords || requestsData.length);
      } else {
        console.warn('[CancellationHistory] API returned unsuccessful response:', response);
        setError(response?.data?.message || 'Failed to fetch cancellation history');
        setRequests([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('[CancellationHistory] Error fetching history:', error);
      setError('Failed to fetch cancellation history. Please try again.');
      setRequests([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter]);

  // Filter requests based on search and date
  useEffect(() => {
    let filtered = [...requests];
    
    if (searchQuery) {
      filtered = filtered.filter(request => 
        request.booking_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        request.court_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        request.user_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (dateRange !== 'all') {
      const now = new Date();
      const today = new Date(now.toLocaleString('en-US', {timeZone: 'Asia/Manila'}));
      
      filtered = filtered.filter(request => {
        if (!request.processed_at) return false;
        
        const processedDate = new Date(request.processed_at);
        const phDate = new Date(processedDate.toLocaleString('en-US', {timeZone: 'Asia/Manila'}));
        
        switch (dateRange) {
          case 'today':
            return phDate.getDate() === today.getDate() && 
                   phDate.getMonth() === today.getMonth() && 
                   phDate.getFullYear() === today.getFullYear();
          case 'week':
            const weekAgo = new Date(today);
            weekAgo.setDate(today.getDate() - 7);
            return phDate >= weekAgo && phDate <= today;
          case 'month':
            return phDate.getMonth() === today.getMonth() && 
                   phDate.getFullYear() === today.getFullYear();
          default:
            return true;
        }
      });
    }
    
    setFilteredRequests(filtered);
  }, [requests, searchQuery, dateRange]);

  useEffect(() => {
    fetchCancellationHistory();
  }, [fetchCancellationHistory]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setError(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setOpenDetailsDialog(true);
  };

  const formatDateToPH = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).utcOffset('+08:00').format('MMM D, YYYY h:mm A');
  };

  const convertTo12Hour = (timeString) => {
    if (!timeString) return '';
    
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    
    const [hours, minutes] = timeString.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) {
      return timeString;
    }
    
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const getCancelTypeDisplay = (cancelType) => {
    const types = {
      emergency: 'Emergency',
      change_plans: 'Change of Plans',
      double_booking: 'Double Booking',
      weather: 'Weather',
      other: 'Other'
    };
    return types[cancelType] || cancelType;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return '#22c55e';
      case 'rejected':
        return '#ef4444';
      default:
        return '#f59e0b';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <ApprovedIcon style={{ color: '#22c55e' }} />;
      case 'rejected':
        return <RejectedIcon style={{ color: '#ef4444' }} />;
      default:
        return <ScheduleIcon style={{ color: '#f59e0b' }} />;
    }
  };

  // Calculate summary statistics
  const summaryStats = {
    totalProcessed: filteredRequests.length,
    approved: filteredRequests.filter(r => r.status === 'approved').length,
    rejected: filteredRequests.filter(r => r.status === 'rejected').length,
    totalRefundAmount: filteredRequests
      .filter(r => r.status === 'approved')
      .reduce((sum, r) => sum + parseFloat(r.refund_amount || 0), 0)
  };

  return (
    <Box className={classes.container} style={containerStyle}>
      {/* Success Message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" style={{ backgroundColor: '#22c55e' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" style={{ backgroundColor: '#ef4444' }}>
          {error}
        </Alert>
      </Snackbar>

      <Fade in timeout={800}>
        <Box>
          {/* Header */}
          <Card className={classes.headerCard}>
            <CardContent style={{ padding: theme.spacing(4) }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 8 }}>
                    Cancellation History
                  </Typography>
                  <Typography variant="h6" style={{ opacity: 0.9 }}>
                    View all processed cancellation requests
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={fetchCancellationHistory}
                  disabled={loading}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    borderRadius: theme.spacing(3),
                  }}
                >
                  Refresh
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <Grid container spacing={3} style={{ marginBottom: theme.spacing(4) }}>
            <Grid item xs={12} md={3}>
              <Slide direction="up" in timeout={1000}>
                <Card className={classes.card}>
                  <CardContent style={{ textAlign: 'center', padding: theme.spacing(3) }}>
                    <HistoryIcon style={{ fontSize: 40, color: '#8b5cf6', marginBottom: 16 }} />
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Total Processed
                    </Typography>
                    <Typography variant="h4" style={{ color: '#8b5cf6', fontWeight: 'bold' }}>
                      {summaryStats.totalProcessed}
                    </Typography>
                    <Typography style={{ color: '#a0aec0', marginTop: 8 }}>
                      All time requests
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Slide direction="up" in timeout={1200}>
                <Card className={classes.card}>
                  <CardContent style={{ textAlign: 'center', padding: theme.spacing(3) }}>
                    <ApprovedIcon style={{ fontSize: 40, color: '#22c55e', marginBottom: 16 }} />
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Approved
                    </Typography>
                    <Typography variant="h4" style={{ color: '#22c55e', fontWeight: 'bold' }}>
                      {summaryStats.approved}
                    </Typography>
                    <Typography style={{ color: '#a0aec0', marginTop: 8 }}>
                      Successful cancellations
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Slide direction="up" in timeout={1400}>
                <Card className={classes.card}>
                  <CardContent style={{ textAlign: 'center', padding: theme.spacing(3) }}>
                    <RejectedIcon style={{ fontSize: 40, color: '#ef4444', marginBottom: 16 }} />
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Rejected
                    </Typography>
                    <Typography variant="h4" style={{ color: '#ef4444', fontWeight: 'bold' }}>
                      {summaryStats.rejected}
                    </Typography>
                    <Typography style={{ color: '#a0aec0', marginTop: 8 }}>
                      Denied requests
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Slide direction="up" in timeout={1600}>
                <Card className={classes.card}>
                  <CardContent style={{ textAlign: 'center', padding: theme.spacing(3) }}>
                    <RefundIcon style={{ fontSize: 40, color: '#06b6d4', marginBottom: 16 }} />
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Total Refunds
                    </Typography>
                    <Typography variant="h4" style={{ color: '#06b6d4', fontWeight: 'bold' }}>
                      ₱{summaryStats.totalRefundAmount.toLocaleString()}
                    </Typography>
                    <Typography style={{ color: '#a0aec0', marginTop: 8 }}>
                      Approved refund amount
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
          </Grid>

          {/* Filters */}
          <Paper className={classes.paper} style={{ padding: theme.spacing(3), marginBottom: theme.spacing(4) }}>
            <Typography variant="h6" gutterBottom style={{ color: '#8b5cf6', fontWeight: 'bold', marginBottom: theme.spacing(3) }}>
              Filter History
            </Typography>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search requests..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={classes.textField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" className={classes.formControl}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" className={classes.formControl}>
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    label="Date Range"
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="week">This Week</MenuItem>
                    <MenuItem value="month">This Month</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Box style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
                  <Typography 
                    variant="body2" 
                    style={{ color: '#a0aec0', alignSelf: 'center' }}
                  >
                    {filteredRequests.length} record{filteredRequests.length !== 1 ? 's' : ''} found
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* History Table */}
          <Slide direction="up" in timeout={1800}>
            <TableContainer component={Paper} className={classes.paper}>
              {loading ? (
                <Box style={{ padding: theme.spacing(4) }}>
                  {[...Array(5)].map((_, index) => (
                    <Box key={index} style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                      <Skeleton variant="circle" width={40} height={40} style={{ backgroundColor: '#2d3748' }} />
                      <Skeleton variant="text" width="30%" height={40} style={{ backgroundColor: '#2d3748' }} />
                      <Skeleton variant="text" width="40%" height={40} style={{ backgroundColor: '#2d3748' }} />
                      <Skeleton variant="text" width="20%" height={40} style={{ backgroundColor: '#2d3748' }} />
                    </Box>
                  ))}
                </Box>
              ) : filteredRequests.length === 0 ? (
                <Box style={{ padding: theme.spacing(6), textAlign: 'center' }}>
                  <AssignmentIcon style={{ fontSize: 60, color: '#4a5568', marginBottom: 16 }} />
                  <Typography variant="h6" style={{ color: '#e2e8f0', marginBottom: 8 }}>
                    No cancellation history found
                  </Typography>
                  <Typography variant="body2" style={{ color: '#a0aec0', marginBottom: 24 }}>
                    No processed cancellation requests match your current filters.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={fetchCancellationHistory}
                    style={{
                      color: '#e2e8f0',
                      borderColor: '#4a5568',
                    }}
                  >
                    Refresh List
                  </Button>
                </Box>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHead}>Customer</TableCell>
                      <TableCell className={classes.tableHead}>Booking</TableCell>
                      <TableCell className={classes.tableHead}>Reason</TableCell>
                      <TableCell className={classes.tableHead}>Processed</TableCell>
                      <TableCell className={classes.tableHead}>Status</TableCell>
                      <TableCell className={classes.tableHead}>Refund</TableCell>
                      <TableCell className={classes.tableHead} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((request, index) => (
                      <Fade in timeout={300 + index * 100} key={request.id}>
                        <TableRow className={classes.tableRow}>
                          <TableCell>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                              <Avatar className={classes.avatar}>
                                {(request.user_name || 'U').charAt(0).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                                  {request.user_name || 'Unknown User'}
                                </Typography>
                                <Typography variant="caption" style={{ color: '#a0aec0' }}>
                                  {request.user_email || 'No email'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                              {request.booking_id}
                            </Typography>
                            <Typography variant="caption" style={{ color: '#a0aec0', display: 'block' }}>
                              {request.court_name}
                            </Typography>
                            <Typography variant="caption" style={{ color: '#a0aec0' }}>
                              {moment(request.reservation_date).format('MMM D, YYYY')}
                            </Typography>
                          </TableCell>
                          
                          <TableCell>
                            <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                              {getCancelTypeDisplay(request.cancel_type)}
                            </Typography>
                            <Typography variant="caption" style={{ color: '#a0aec0' }}>
                              {request.reason.length > 50 ? `${request.reason.substring(0, 50)}...` : request.reason}
                            </Typography>
                          </TableCell>
                          
                          <TableCell>
                            <Typography variant="body2" style={{ color: '#e2e8f0', marginBottom: 4 }}>
                              {formatDateToPH(request.processed_at)}
                            </Typography>
                            <Typography variant="caption" style={{ color: '#a0aec0' }}>
                              {request.admin_name || 'System'}
                            </Typography>
                          </TableCell>
                          
                          <TableCell>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {getStatusIcon(request.status)}
                              <Chip 
                                label={request.status.toUpperCase()} 
                                size="small" 
                                style={{
                                  backgroundColor: `rgba(${getStatusColor(request.status).slice(1, 3)}, ${getStatusColor(request.status).slice(3, 5)}, ${getStatusColor(request.status).slice(5, 7)}, 0.1)`,
                                  color: getStatusColor(request.status),
                                  border: `1px solid ${getStatusColor(request.status)}`
                                }}
                              />
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            {request.status === 'approved' ? (
                              <Box>
                                <Typography variant="body2" style={{ color: '#22c55e', fontWeight: 'bold' }}>
                                  ₱{parseFloat(request.refund_amount || 0).toLocaleString()}
                                </Typography>
                                <Chip 
                                  label={request.refund_status || 'pending'}
                                  size="small"
                                  style={{
                                    backgroundColor: request.refund_status === 'completed' 
                                      ? 'rgba(34, 197, 94, 0.1)' 
                                      : 'rgba(245, 158, 11, 0.1)',
                                    color: request.refund_status === 'completed' 
                                      ? '#22c55e' 
                                      : '#f59e0b',
                                    fontSize: '0.7rem',
                                    marginTop: 4
                                  }}
                                />
                              </Box>
                            ) : (
                              <Typography variant="body2" style={{ color: '#6b7280' }}>
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          
                          <TableCell align="center">
                            <Tooltip title="View Details">
                              <IconButton 
                                style={{ color: '#8b5cf6' }}
                                onClick={() => handleViewDetails(request)}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      </Fade>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              {filteredRequests.length > 0 && (
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredRequests.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                  style={{
                    color: '#e2e8f0',
                    borderTop: '1px solid #2d3748',
                  }}
                />
              )}
            </TableContainer>
          </Slide>
        </Box>
      </Fade>

      {/* Details Dialog */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="lg"
        fullWidth
        classes={{ paper: classes.dialog }}
      >
        {selectedRequest && (
          <>
            <DialogTitle style={{ 
              borderBottom: '1px solid #2d3748',
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              color: '#8b5cf6'
            }}>
              <HistoryIcon />
              Cancellation Request Details
              <Typography variant="body2" style={{ marginLeft: 'auto', color: '#a0aec0' }}>
                ID: #{selectedRequest.id}
              </Typography>
            </DialogTitle>
            
            <DialogContent style={{ padding: theme.spacing(4) }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper className={classes.paper} style={{ padding: theme.spacing(3), height: '100%' }}>
                    <Typography variant="h6" gutterBottom style={{ 
                      color: '#8b5cf6', 
                      fontWeight: 'bold', 
                      borderBottom: '1px solid #2d3748', 
                      paddingBottom: 8, 
                      marginBottom: 16 
                    }}>
                      Request Information
                    </Typography>
                    
                    <Box style={{ marginBottom: 16 }}>
                      <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Customer:</Typography>
                      <Typography variant="body1" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                        {selectedRequest.user_name}
                      </Typography>
                      <Typography variant="caption" style={{ color: '#a0aec0' }}>
                        {selectedRequest.user_email}
                      </Typography>
                    </Box>
                    
                    <Box style={{ marginBottom: 16 }}>
                      <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Booking ID:</Typography>
                      <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                        {selectedRequest.booking_id}
                      </Typography>
                    </Box>
                    
                    <Box style={{ marginBottom: 16 }}>
                      <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Cancellation Type:</Typography>
                      <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                        {getCancelTypeDisplay(selectedRequest.cancel_type)}
                      </Typography>
                    </Box>
                    
                    <Box style={{ marginBottom: 16 }}>
                      <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Status:</Typography>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                        {getStatusIcon(selectedRequest.status)}
                        <Chip 
                          label={selectedRequest.status.toUpperCase()} 
                          size="small" 
                          style={{
                            backgroundColor: `rgba(${getStatusColor(selectedRequest.status).slice(1, 3)}, ${getStatusColor(selectedRequest.status).slice(3, 5)}, ${getStatusColor(selectedRequest.status).slice(5, 7)}, 0.1)`,
                            color: getStatusColor(selectedRequest.status),
                            border: `1px solid ${getStatusColor(selectedRequest.status)}`
                          }}
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper className={classes.paper} style={{ padding: theme.spacing(3), height: '100%' }}>
                    <Typography variant="h6" gutterBottom style={{ 
                      color: '#8b5cf6', 
                      fontWeight: 'bold', 
                      borderBottom: '1px solid #2d3748', 
                      paddingBottom: 8, 
                      marginBottom: 16 
                    }}>
                      Booking Details
                    </Typography>
                    
                    <Box style={{ marginBottom: 16 }}>
                      <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Court:</Typography>
                      <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                        {selectedRequest.court_name}
                      </Typography>
                      <Typography variant="caption" style={{ color: '#a0aec0' }}>
                        {selectedRequest.court_location}
                      </Typography>
                    </Box>
                    
                    <Box style={{ marginBottom: 16 }}>
                      <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Date & Time:</Typography>
                      <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                        {moment(selectedRequest.reservation_date).format('MMMM D, YYYY')}
                      </Typography>
                      <Typography variant="body2" style={{ color: '#e2e8f0' }}>
                        {convertTo12Hour(selectedRequest.time_from)} - {convertTo12Hour(selectedRequest.time_to)}
                      </Typography>
                    </Box>
                    
                    <Box style={{ marginBottom: 16 }}>
                      <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Total Amount:</Typography>
                      <Typography variant="body1" style={{ fontWeight: 'bold', color: '#06b6d4' }}>
                        ₱{parseFloat(selectedRequest.total_amount || 0).toLocaleString()}
                      </Typography>
                    </Box>
                    
                    {selectedRequest.status === 'approved' && (
                      <Box style={{ marginBottom: 16 }}>
                        <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Refund Amount:</Typography>
                        <Typography variant="body1" style={{ fontWeight: 'bold', color: '#22c55e' }}>
                          ₱{parseFloat(selectedRequest.refund_amount || 0).toLocaleString()}
                        </Typography>
                        <Chip 
                          label={`Refund ${selectedRequest.refund_status || 'pending'}`}
                          size="small"
                          style={{
                            backgroundColor: selectedRequest.refund_status === 'completed' 
                              ? 'rgba(34, 197, 94, 0.1)' 
                              : 'rgba(245, 158, 11, 0.1)',
                            color: selectedRequest.refund_status === 'completed' 
                              ? '#22c55e' 
                              : '#f59e0b',
                            marginTop: 8
                          }}
                        />
                      </Box>
                    )}
                  </Paper>
                </Grid>
                
                <Grid item xs={12}>
                  <Paper className={classes.paper} style={{ padding: theme.spacing(3) }}>
                    <Typography variant="h6" gutterBottom style={{ 
                      color: '#8b5cf6', 
                      fontWeight: 'bold', 
                      borderBottom: '1px solid #2d3748', 
                      paddingBottom: 8, 
                      marginBottom: 16 
                    }}>
                      Request Details
                    </Typography>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box style={{ marginBottom: 16 }}>
                          <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Reason for Cancellation:</Typography>
                          <Box style={{ 
                            backgroundColor: 'rgba(139, 92, 246, 0.1)', 
                            padding: 12, 
                            borderRadius: 4,
                            border: '1px solid rgba(139, 92, 246, 0.2)',
                            marginTop: 8
                          }}>
                            <Typography variant="body2" style={{ color: '#e2e8f0' }}>
                              {selectedRequest.reason}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {selectedRequest.additional_notes && (
                          <Box style={{ marginBottom: 16 }}>
                            <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Additional Notes:</Typography>
                            <Box style={{ 
                              backgroundColor: 'rgba(6, 182, 212, 0.1)', 
                              padding: 12, 
                              borderRadius: 4,
                              border: '1px solid rgba(6, 182, 212, 0.2)',
                              marginTop: 8
                            }}>
                              <Typography variant="body2" style={{ color: '#e2e8f0' }}>
                                {selectedRequest.additional_notes}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Box style={{ marginBottom: 16 }}>
                          <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Processing Timeline:</Typography>
                          <Box style={{ marginTop: 8 }}>
                            <Typography variant="body2" style={{ color: '#e2e8f0', marginBottom: 4 }}>
                              <strong>Requested:</strong> {formatDateToPH(selectedRequest.requested_at)}
                            </Typography>
                            <Typography variant="body2" style={{ color: '#e2e8f0', marginBottom: 4 }}>
                              <strong>Processed:</strong> {formatDateToPH(selectedRequest.processed_at)}
                            </Typography>
                            <Typography variant="body2" style={{ color: '#e2e8f0' }}>
                              <strong>Processed by:</strong> {selectedRequest.admin_name || 'System'}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {selectedRequest.admin_notes && (
                          <Box>
                            <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Admin Notes:</Typography>
                            <Box style={{ 
                              backgroundColor: selectedRequest.status === 'approved' 
                                ? 'rgba(34, 197, 94, 0.1)' 
                                : 'rgba(239, 68, 68, 0.1)', 
                              padding: 12, 
                              borderRadius: 4,
                              border: `1px solid ${selectedRequest.status === 'approved' 
                                ? 'rgba(34, 197, 94, 0.2)' 
                                : 'rgba(239, 68, 68, 0.2)'}`,
                              marginTop: 8
                            }}>
                              <Typography variant="body2" style={{ color: '#e2e8f0' }}>
                                {selectedRequest.admin_notes}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            
            <DialogActions style={{ padding: theme.spacing(3), borderTop: '1px solid #2d3748' }}>
              <Button 
                onClick={() => setOpenDetailsDialog(false)}
                style={{ color: '#a0aec0' }}
                startIcon={<CloseIcon />}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}